import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Smile, Frown, Meh, Activity, Droplets, Moon, FileText,
  Loader2, ChevronDown, ChevronUp, Plus,
} from "lucide-react";

/* ============== Shared expandable card ============== */
function Section({ title, icon: Icon, accent, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card-surface overflow-hidden" data-testid={`wellness-${title.toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 hover:bg-clinical-bg/40 transition"
        data-testid={`wellness-${title.toLowerCase()}-toggle`}
      >
        <div className="flex items-center gap-3">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ background: accent + "1a", color: accent }}
          >
            <Icon size={16} />
          </span>
          <span className="font-medium text-clinical-text">{title}</span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-clinical-border/40 pt-3">{children}</div>}
    </div>
  );
}

/* ============== Mood ============== */
const MOOD_OPTIONS = [
  { rating: 2, label: "Agitated", icon: Frown, color: "#EF4444" },
  { rating: 1, label: "Restless", icon: Meh, color: "#F59E0B" },
  { rating: 0, label: "Calm", icon: Smile, color: "#22C55E" },
  { rating: -1, label: "Low", icon: Meh, color: "#3B82F6" },
  { rating: -2, label: "Withdrawn", icon: Frown, color: "#8B5CF6" },
];

function MoodForm({ clientId, onSaved }) {
  const [saving, setSaving] = useState(false);
  const submit = async (rating) => {
    setSaving(true);
    try {
      const hr = new Date().getHours();
      const shift = hr < 12 ? "morning" : hr < 17 ? "afternoon" : hr < 21 ? "evening" : "night";
      await api.post(`/clients/${clientId}/mood`, { shift, rating });
      toast.success("Mood logged");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grid grid-cols-5 gap-2">
      {MOOD_OPTIONS.map((m) => {
        const Icon = m.icon;
        return (
          <button
            key={m.rating}
            type="button"
            disabled={saving}
            onClick={() => submit(m.rating)}
            className="flex flex-col items-center gap-1 rounded-lg border border-clinical-border py-3 transition hover:scale-105 active:scale-95 disabled:opacity-50"
            style={{ borderColor: m.color + "55" }}
            data-testid={`mood-btn-${m.rating}`}
          >
            <Icon size={20} style={{ color: m.color }} />
            <span className="text-[10px] text-clinical-subtext">{m.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ============== Fluid intake ============== */
const FLUID_PRESETS = [
  { type: "water", amount_ml: 250, label: "Water glass" },
  { type: "water", amount_ml: 500, label: "Water bottle" },
  { type: "juice", amount_ml: 200, label: "Juice" },
  { type: "tea", amount_ml: 250, label: "Tea/Coffee" },
  { type: "ensure", amount_ml: 240, label: "Ensure" },
];

function FluidForm({ clientId, onSaved }) {
  const [totals, setTotals] = useState([]);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const loadTotals = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/fluid/daily-totals`, { params: { days: 7 } });
      setTotals(data.days || []);
    } catch { /* swallow */ }
  }, [clientId]);

  useEffect(() => { loadTotals(); }, [loadTotals]);

  const submit = async (preset) => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/fluid`, preset);
      toast.success(`Logged ${preset.amount_ml}mL of ${preset.type}`);
      await loadTotals();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const todayTotal = totals.find((d) => d.date === today)?.total_ml ?? 0;
  const target = 1500; // ml/day baseline for dehydration tracking
  const pct = Math.min(100, Math.round((todayTotal / target) * 100));

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-xs text-clinical-subtext">Today</span>
          <span className="text-sm font-medium text-clinical-text">{todayTotal} / {target} mL</span>
        </div>
        <div className="h-2 rounded-full bg-clinical-border/50 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${pct}%`,
              background: todayTotal < target * 0.5 ? "#EF4444" : todayTotal < target ? "#F59E0B" : "#22C55E",
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {FLUID_PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            disabled={saving}
            onClick={() => submit(p)}
            className="rounded-lg border border-clinical-border bg-white px-2 py-2 text-xs hover:border-clinical-accent disabled:opacity-50"
            data-testid={`fluid-preset-${p.type}-${p.amount_ml}`}
          >
            <div className="font-medium">{p.label}</div>
            <div className="text-[10px] text-clinical-subtext">{p.amount_ml} mL</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============== Sleep ============== */
const SLEEP_QUALITY = [
  { v: "good", label: "Good", color: "#22C55E" },
  { v: "fair", label: "Fair", color: "#F59E0B" },
  { v: "poor", label: "Poor", color: "#EF4444" },
];

function SleepForm({ clientId, onSaved }) {
  const [hours, setHours] = useState(7);
  const [quality, setQuality] = useState("good");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const sleep_date = new Date().toISOString().slice(0, 10);
      const continuous_block = hours < 1 ? "<1h" : hours < 2 ? "1-2h" : hours < 4 ? "2-4h" : "4+h";
      await api.post(`/clients/${clientId}/sleep`, { sleep_date, quality, total_hours: hours, continuous_block });
      toast.success("Sleep logged");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <label htmlFor="sleep-hours" className="text-xs text-clinical-subtext">Hours slept last night</label>
          <span className="text-sm font-medium">{hours} h</span>
        </div>
        <input
          id="sleep-hours"
          type="range"
          min="0"
          max="14"
          step="0.5"
          value={hours}
          onChange={(e) => setHours(Number(e.target.value))}
          className="w-full"
          data-testid="sleep-hours-slider"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        {SLEEP_QUALITY.map((q) => (
          <button
            key={q.v}
            type="button"
            onClick={() => setQuality(q.v)}
            className={`rounded-lg border py-2 text-xs font-medium transition ${
              quality === q.v ? "text-white" : "text-clinical-subtext hover:text-clinical-text"
            }`}
            style={{
              background: quality === q.v ? q.color : "white",
              borderColor: q.color,
            }}
            data-testid={`sleep-quality-${q.v}`}
          >
            {q.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="w-full rounded-lg bg-clinical-accent py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        data-testid="sleep-save-btn"
      >
        {saving ? <Loader2 size={14} className="inline animate-spin" /> : "Save sleep"}
      </button>
    </div>
  );
}

/* ============== Bowel ============== */
const BRISTOL = [
  { v: 1, label: "Hard lumps", desc: "Severe constipation" },
  { v: 2, label: "Lumpy sausage", desc: "Mild constipation" },
  { v: 3, label: "Cracked sausage", desc: "Normal" },
  { v: 4, label: "Smooth sausage", desc: "Ideal" },
  { v: 5, label: "Soft blobs", desc: "Lacking fibre" },
  { v: 6, label: "Mushy", desc: "Mild diarrhea" },
  { v: 7, label: "Liquid", desc: "Severe diarrhea" },
];

function BowelForm({ clientId, onSaved }) {
  const [bristol, setBristol] = useState(4);
  const [size, setSize] = useState("medium");
  const [color, setColor] = useState("normal");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const consistency = bristol <= 2 ? "hard" : bristol <= 4 ? "formed" : bristol <= 6 ? "loose" : "watery";
      await api.post(`/clients/${clientId}/bowel`, {
        size, consistency, color, bristol_scale: bristol,
        successful_outcome: true, notes,
      });
      toast.success("Bowel movement logged");
      setNotes("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-clinical-subtext mb-2">Bristol Stool Scale</div>
        <div className="flex gap-1 overflow-x-auto">
          {BRISTOL.map((b) => (
            <button
              key={b.v}
              type="button"
              onClick={() => setBristol(b.v)}
              className={`flex min-w-[64px] flex-col items-center gap-0.5 rounded-lg border px-2 py-2 text-[10px] transition ${
                bristol === b.v
                  ? "border-clinical-accent bg-clinical-accent/10 text-clinical-accent font-medium"
                  : "border-clinical-border text-clinical-subtext"
              }`}
              data-testid={`bristol-${b.v}`}
            >
              <span className="text-sm font-bold">{b.v}</span>
              <span className="leading-tight text-center">{b.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-1 text-[10px] text-clinical-subtext">
          Type {bristol}: {BRISTOL[bristol - 1].desc}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="bowel-size" className="text-xs text-clinical-subtext">Size</label>
          <select
            id="bowel-size"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            className="mt-1 w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="bowel-size"
          >
            <option value="minimal">Minimal</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
        <div>
          <label htmlFor="bowel-color" className="text-xs text-clinical-subtext">Colour</label>
          <select
            id="bowel-color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="mt-1 w-full rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="bowel-color"
          >
            <option value="normal">Normal</option>
            <option value="light_brown">Light brown</option>
            <option value="dark_brown">Dark brown</option>
            <option value="black">Black (⚠)</option>
            <option value="red">Red (⚠)</option>
            <option value="off">Off-colour</option>
          </select>
        </div>
      </div>
      <input
        type="text"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid="bowel-notes"
      />
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className="w-full rounded-lg bg-clinical-accent py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        data-testid="bowel-save-btn"
      >
        {saving ? <Loader2 size={14} className="inline animate-spin" /> : "Save bowel entry"}
      </button>
    </div>
  );
}

/* ============== Incidents (fall + behaviour) ============== */
function IncidentForm({ clientId, kind, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const isFall = kind === "fall";
  const [form, setForm] = useState(
    isFall
      ? {
          location: "", description: "", witnessed: false, injury_severity: "none",
          body_part: "", er_transferred: false, family_notified: false, manager_notified: false,
          doctor_recommendations: "", prevention_plan: "",
        }
      : {
          location: "", description: "", antecedent: "", behaviour_duration_minutes: 5,
          intervention: "", aftermath: "", function: "unknown",
          prn_administered: false, family_notified: false, manager_notified: false, prevention_plan: "",
        }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/incidents/${kind}`, form);
      toast.success(`${isFall ? "Fall" : "Behaviour"} incident reported`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`incident-form-${kind}`}>
      <input
        type="text" value={form.location} onChange={(e) => set("location", e.target.value)}
        placeholder="Where did it happen?"
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        data-testid={`incident-${kind}-location`}
      />
      {!isFall && (
        <textarea
          value={form.antecedent} onChange={(e) => set("antecedent", e.target.value)}
          rows={2} placeholder="What happened just BEFORE?"
          className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
          data-testid={`incident-${kind}-antecedent`}
        />
      )}
      <textarea
        value={form.description} onChange={(e) => set("description", e.target.value)}
        rows={3} placeholder={`Describe the ${isFall ? "fall" : "behaviour"}`}
        className="w-full rounded-lg border border-clinical-border bg-white px-3 py-2 text-sm"
        required
        data-testid={`incident-${kind}-description`}
      />
      {isFall ? (
        <div className="grid grid-cols-2 gap-2">
          <select
            value={form.injury_severity} onChange={(e) => set("injury_severity", e.target.value)}
            className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="incident-fall-severity"
          >
            <option value="none">No injury</option>
            <option value="minor">Minor</option>
            <option value="moderate">Moderate</option>
            <option value="severe">Severe</option>
          </select>
          <input
            type="text" value={form.body_part} onChange={(e) => set("body_part", e.target.value)}
            placeholder="Body part injured"
            className="rounded-lg border border-clinical-border bg-white px-3 py-1.5 text-sm"
            data-testid="incident-fall-body-part"
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number" min="0" max="240" value={form.behaviour_duration_minutes}
            onChange={(e) => set("behaviour_duration_minutes", Number(e.target.value))}
            placeholder="Duration (minutes)"
            className="rounded-lg border border-clinical-border bg-white px-3 py-1.5 text-sm"
            data-testid="incident-behaviour-duration"
          />
          <select
            value={form.function} onChange={(e) => set("function", e.target.value)}
            className="rounded-lg border border-clinical-border bg-white px-2 py-1.5 text-sm"
            data-testid="incident-behaviour-function"
          >
            <option value="unknown">Function unknown</option>
            <option value="escape">Escape/avoidance</option>
            <option value="social">Attention/social</option>
            <option value="tangible">Tangible gain</option>
            <option value="sensory">Sensory</option>
          </select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.family_notified} onChange={(e) => set("family_notified", e.target.checked)} data-testid={`incident-${kind}-family-notified`} />
          Family notified
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.manager_notified} onChange={(e) => set("manager_notified", e.target.checked)} data-testid={`incident-${kind}-manager-notified`} />
          Manager notified
        </label>
        {isFall ? (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.er_transferred} onChange={(e) => set("er_transferred", e.target.checked)} data-testid="incident-fall-er-transferred" />
            ER transferred
          </label>
        ) : (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.prn_administered} onChange={(e) => set("prn_administered", e.target.checked)} data-testid="incident-behaviour-prn" />
            PRN administered
          </label>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-lg border border-clinical-border py-2 text-sm font-medium"
          data-testid={`incident-${kind}-cancel`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          data-testid={`incident-${kind}-submit`}
        >
          {saving ? <Loader2 size={14} className="inline animate-spin" /> : `Report ${isFall ? "fall" : "behaviour"}`}
        </button>
      </div>
    </div>
  );
}

/* ============== Master panel ============== */
export default function WellnessPanel({ clientId }) {
  const [incidentKind, setIncidentKind] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((r) => r + 1);

  return (
    <div className="space-y-3" data-testid="wellness-panel">
      <Section title="Mood" icon={Smile} accent="#22C55E" defaultOpen>
        <MoodForm clientId={clientId} onSaved={bump} key={`mood-${refresh}`} />
      </Section>
      <Section title="Fluid" icon={Droplets} accent="#3B82F6">
        <FluidForm clientId={clientId} onSaved={bump} key={`fluid-${refresh}`} />
      </Section>
      <Section title="Sleep" icon={Moon} accent="#8B5CF6">
        <SleepForm clientId={clientId} onSaved={bump} key={`sleep-${refresh}`} />
      </Section>
      <Section title="Bowel" icon={FileText} accent="#A16207">
        <BowelForm clientId={clientId} onSaved={bump} key={`bowel-${refresh}`} />
      </Section>
      <div className="card-surface p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-red-600" />
            <span className="font-medium text-clinical-text">Report incident</span>
          </div>
        </div>
        {incidentKind ? (
          <IncidentForm
            clientId={clientId}
            kind={incidentKind}
            onClose={() => setIncidentKind(null)}
            onSaved={bump}
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setIncidentKind("fall")}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
              data-testid="open-fall-form"
            >
              <Plus size={14} /> Fall
            </button>
            <button
              type="button"
              onClick={() => setIncidentKind("behaviour")}
              className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
              data-testid="open-behaviour-form"
            >
              <Plus size={14} /> Behaviour
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
